import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlassButton } from '../components/LiquidComponents';
import { ProjectCard } from '../components/ProjectCard';
import { useData } from '../contexts/DataContext';
import { getProjectPath } from '../utils/projectSlug';

export const Home: React.FC = () => {
  const { projects, siteConfig, loading } = useData();

  const featuredProjects = projects.slice(0, 3);

  const heroSubheadline = siteConfig?.heroSubheadline || "Building the future through scalable technology and strategic innovation.";
  const heroCtaText = siteConfig?.heroCtaText || "Explore Portfolio";

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-500"></div>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="min-h-[75vh] md:min-h-[calc(100vh-13rem)] flex flex-col justify-center items-center text-center px-4 md:px-6 relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[500px] md:h-[500px] bg-blue-500/10 dark:bg-blue-600/20 rounded-full blur-[80px] md:blur-[120px] -z-10 [.nardogrey_&]:hidden" />
        
        {/* Optimized Chrome Text with CSS Animation */}
        <div className="w-full max-w-7xl mx-auto flex justify-center items-center py-6">
          <style>
            {`
              @keyframes chromeFlow {
                0% {
                  background-position: 50% 0%;
                }
                100% {
                  background-position: 50% -200%;
                }
              }
              
              .chrome-text {
                background: linear-gradient(
                  180deg,
                  #b8b8b8 0%,
                  #e8e8e8 5%,
                  #ffffff 10%,
                  #f0f0f0 15%,
                  #c0c0c0 20%,
                  #a0a0a0 25%,
                  #808080 30%,
                  #909090 35%,
                  #b0b0b0 40%,
                  #d0d0d0 45%,
                  #e8e8e8 50%,
                  #ffffff 55%,
                  #f0f0f0 60%,
                  #d0d0d0 65%,
                  #b0b0b0 70%,
                  #989898 75%,
                  #888888 80%,
                  #a0a0a0 85%,
                  #c0c0c0 90%,
                  #d8d8d8 95%,
                  #b8b8b8 100%
                );
                background-size: 100% 200%;
                -webkit-background-clip: text;
                background-clip: text;
                -webkit-text-fill-color: transparent;
                animation: chromeFlow 15s linear infinite;
                will-change: background-position;
                font-family: 'Inter', sans-serif;
                font-weight: 900;
                letter-spacing: -0.04em;
                line-height: 0.9;
                text-shadow: 2px 4px 8px rgba(0, 0, 0, 0.2);
              }
              
              /* Mobile optimization */
              @media (max-width: 768px) {
                .chrome-text {
                  animation: chromeFlow 12s linear infinite;
                }
              }
            `}
          </style>
          
          <h1 className="chrome-text text-[60px] sm:text-[100px] md:text-[140px] lg:text-[160px] text-center">
            <div>VISION</div>
            <div>EXECUTION</div>
            <div>EXIT</div>
          </h1>
        </div>
        
        <p className="max-w-xs sm:max-w-2xl text-base md:text-lg text-zinc-600 dark:text-zinc-400 [.nardogrey_&]:text-white mb-4 md:mb-6 font-light leading-relaxed animate-in fade-in slide-in-from-bottom-12 duration-1000 delay-200">
          {heroSubheadline}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto px-6 sm:px-0 animate-in fade-in slide-in-from-bottom-16 duration-1000 delay-300">
          <GlassButton to="/portfolio" className="w-full sm:w-auto">
            {heroCtaText} <ArrowRight size={18} />
          </GlassButton>
          <GlassButton variant="secondary" to="/about" className="w-full sm:w-auto">
            About Me
          </GlassButton>
        </div>
      </section>

      {/* Project-name ticker — continuous right-to-left marquee under the hero.
          Seamless loop: the track holds the name list twice; CSS slides it 0 → -50%.
          Hover changes only the name color; the track never pauses (see index.css). */}
      {projects.length > 0 && (
        <section aria-label="Projects" className="py-6 border-y border-black/5 dark:border-white/5 [.nardogrey_&]:border-white/10">
          <div className="marquee">
            <div className="marquee-track">
              {[0, 1].map((copy) => (
                <ul
                  key={copy}
                  className="marquee-group"
                  aria-hidden={copy === 1 ? true : undefined}
                >
                  {projects.map((project) => (
                    <li key={`${copy}-${project.id}`} className="flex items-center">
                      <Link
                        to={getProjectPath(project)}
                        tabIndex={copy === 1 ? -1 : undefined}
                        className="px-6 md:px-8 text-xs md:text-sm font-light uppercase tracking-[0.2em] whitespace-nowrap text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-300 hover:text-zinc-900 dark:hover:text-white [.nardogrey_&]:hover:text-white transition-colors"
                      >
                        {project.title || project.name}
                      </Link>
                      <span aria-hidden className="select-none text-zinc-400/50 dark:text-zinc-600 [.nardogrey_&]:text-zinc-500">·</span>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Projects */}
      <section className="container mx-auto px-4 md:px-6 py-12 md:py-24">
        <div className="flex justify-between items-end mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-light text-zinc-900 dark:text-white">Selected Works</h2>
          <Link
            to="/portfolio"
            className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors flex items-center gap-1 [.nardogrey_&]:text-zinc-400 [.nardogrey_&]:hover:text-white"
          >
            View All <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {featuredProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
        
        {!loading && featuredProjects.length === 0 && (
          <div className="text-center py-20 text-zinc-500">
            <p>No projects found in the database.</p>
          </div>
        )}

        {/* Manifesto — bold typographic statement beneath Selected Works.
            Primary: large, heavy, uppercase, near-white across all themes, set
            in three lines for rhythm. Secondary: a small, muted coda set off by
            a hairline. The size contrast is the whole point. */}
        <div className="mt-20 md:mt-32">
          <p className="font-black uppercase tracking-tight leading-[0.95] text-zinc-900 dark:text-white [.nardogrey_&]:text-white text-[clamp(1.125rem,6vw,6rem)]">
            Ambitious in vision.<br />
            Conservative in claims.<br />
            Precise in execution.
          </p>
          {/* Signature coda — muted, tracked, right-anchored (echoes the right-aligned
              year index used across About). No rule/divider bar; the long sentence
              wraps to ~2 lines within a right-aligned max-width block.
              Swap alternates (shorter):
                VISION, EXECUTED TO CONCLUSION.
                EXECUTION IS THE WHOLE ARGUMENT.
                A VISION IS ONLY AS REAL AS ITS EXIT. */}
          <p className="mt-8 md:mt-10 ml-auto max-w-xs md:max-w-sm text-right uppercase tracking-[0.15em] leading-relaxed text-xs md:text-sm text-zinc-500 [.nardogrey_&]:text-zinc-400">
            A VISION ONLY MATTERS WHEN IT IS BROUGHT TO ITS ABSOLUTE CONCLUSION.
          </p>
        </div>
      </section>
    </div>
  );
};
